import { Button } from '../../../../../../components/Toolkit/Button/Button';
import { type RevealPhoneNumberProps } from '../../../../../../components/PageWrappers/SharedComponents/RevealPhoneNumber/RevealPhoneNumber';
import { ShowNumberBottomSheet } from '../subcomponents/ShowNumberBottomSheet';
import { EmailDealerOrSeller } from '../../EmailDealerOrSeller/EmailDealerOrSeller';

import { type EmailDealerOrSellerProps } from '../../EmailDealerOrSeller/EmailDealerOrSeller.typed';
import { MailOutlineIc } from '@dsch/dd-icons';

import { useWhatsappAction } from '../subcomponents/useWhatsAppAction';
import { SELLER_TYPES } from '../../../types';

import styled from 'styled-components';

type Variant1Props = {
  displayContactDealershipBottomSheet: boolean;
  phoneResponse: boolean;
  emailResponse: boolean;
  phoneContactProps: RevealPhoneNumberProps;
  emailSellerProps: EmailDealerOrSellerProps;
  whatsappResponse: boolean;
  adId: number;
  priceEuro?: number | null;
  sellerType?: string;
  sectionName?: string;
  price?: string | null;
};

const Variant1Container = styled.div<{ ctaCount: number }>`
  display: grid;
  gap: 8px;
  grid-template-columns: ${({ ctaCount }) =>
    `${'min-content '.repeat(Math.max(ctaCount - 1, 0)).trim()} 1fr`};
`;

const SEmailDealerOrSeller = styled(EmailDealerOrSeller)<{ ctaCount: number }>`
  padding: ${({ theme }) => `0 ${theme.spacing.S12}`};
  ${({ ctaCount }) => ctaCount === 2 && `min-width: 124px;`}
`;

const SWhatsappButton = styled(Button)<{ ctaCount: number }>`
  padding: ${({ theme }) => `0 ${theme.spacing.S12}`};
  ${({ ctaCount }) => ctaCount === 2 && `min-width: 124px;`}
`;

const SPhoneCtaWrapper = styled.div<{ ctaCount: number }>`
  ${({ ctaCount }) => ctaCount === 2 && `min-width: 124px;`}
`;

type EmailCtaProps = {
  emailSellerProps: EmailDealerOrSellerProps;
  isPrivateSeller: boolean;
  phoneResponse: boolean;
  whatsappResponse: boolean;
  ctaCount: number;
};

const EmailCta = ({
  emailSellerProps,
  isPrivateSeller,
  phoneResponse,
  whatsappResponse,
  ctaCount,
}: EmailCtaProps) => (
  <SEmailDealerOrSeller
    {...emailSellerProps}
    ofType={
      !isPrivateSeller && (phoneResponse || whatsappResponse)
        ? 'SECONDARY'
        : 'PRIMARY'
    }
    ctaCount={ctaCount}
  >
    {ctaCount === 3 ? (
      <MailOutlineIc width={20} height={20} />
    ) : isPrivateSeller ? (
      'Message'
    ) : (
      'Email'
    )}
  </SEmailDealerOrSeller>
);

type WhatsappCtaProps = {
  isPrivateSeller: boolean;
  phoneResponse: boolean;
  emailResponse: boolean;
  onClick: () => void;
  isLoading: boolean;
  ctaCount: number;
};

const WhatsappCta = ({
  isPrivateSeller,
  phoneResponse,
  emailResponse,
  onClick,
  isLoading,
  ctaCount,
}: WhatsappCtaProps) => (
  <SWhatsappButton
    ofType={
      (isPrivateSeller ? emailResponse : phoneResponse)
        ? 'SECONDARY'
        : 'PRIMARY'
    }
    onClick={onClick}
    disabled={isLoading}
    ctaCount={ctaCount}
  >
    WhatsApp
  </SWhatsappButton>
);

type PhoneCtaProps = {
  phoneContactProps: RevealPhoneNumberProps;
  isPrivateSeller: boolean;
  emailResponse: boolean;
  whatsappResponse: boolean;
  ctaCount: number;
};

const PhoneCta = ({
  phoneContactProps,
  isPrivateSeller,
  emailResponse,
  whatsappResponse,
  ctaCount,
}: PhoneCtaProps) => (
  <SPhoneCtaWrapper ctaCount={ctaCount}>
    <ShowNumberBottomSheet
      key={phoneContactProps.adId}
      {...phoneContactProps}
      isPrimaryButton={
        isPrivateSeller
          ? !emailResponse && !whatsappResponse
          : phoneContactProps.isPrimaryButton
      }
    >
      Show phone
    </ShowNumberBottomSheet>
  </SPhoneCtaWrapper>
);

const Variant1 = (props: Variant1Props) => {
  const {
    emailResponse,
    emailSellerProps,
    phoneResponse,
    phoneContactProps,
    whatsappResponse,
    adId,
    priceEuro,
    sellerType,
    sectionName,
    price,
  } = props;

  const { onWhatsAppButtonClick, isLoading } = useWhatsappAction({
    adId,
    priceEuro,
    sellerType,
    sectionName,
    price,
  });

  const ctaCount = [phoneResponse, emailResponse, whatsappResponse].filter(
    Boolean,
  ).length;

  const isPrivateSeller = sellerType === SELLER_TYPES.PRIVATE;

  const emailCtaProps = {
    emailSellerProps,
    isPrivateSeller,
    phoneResponse,
    whatsappResponse,
    ctaCount,
  };
  const whatsappCtaProps = {
    isPrivateSeller,
    phoneResponse,
    emailResponse,
    onClick: onWhatsAppButtonClick,
    isLoading,
    ctaCount,
  };
  const phoneCtaProps = {
    phoneContactProps,
    isPrivateSeller,
    emailResponse,
    whatsappResponse,
    ctaCount,
  };

  return (
    <Variant1Container ctaCount={ctaCount}>
      {isPrivateSeller ? (
        <>
          {phoneResponse && <PhoneCta {...phoneCtaProps} />}
          {whatsappResponse && <WhatsappCta {...whatsappCtaProps} />}
          {emailResponse && <EmailCta {...emailCtaProps} />}
        </>
      ) : (
        <>
          {emailResponse && <EmailCta {...emailCtaProps} />}
          {whatsappResponse && <WhatsappCta {...whatsappCtaProps} />}
          {phoneResponse && <PhoneCta {...phoneCtaProps} />}
        </>
      )}
    </Variant1Container>
  );
};

export { Variant1 };
